import * as Yup from "yup"
import { Link } from "react-router-dom"
import CreatableSelect from "react-select/creatable"
import { useField, useFormikContext } from "formik"
import type { MultiValue } from "react-select"

import CatchCard from "~/library/CatchCard"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import FormikRadioGroup from "~/library/FormikRadioGroup"
import ImageUpload from "~/library/ImageUpload"
import StripeConnectSection from "~/components/StripeConnectSection"
import type Venue from "~/types/Venue"
import type { Image } from "~/types/Image"
import { reactSelectStyles } from "~/utils/reactSelectStyles"

const decimalRateField = (fieldLabel: string, enabledField: string) =>
  Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) return null
      return value
    })
    .nullable()
    .when(enabledField, {
      is: true,
      then: (schema) =>
        schema
          .required(`${fieldLabel} is required`)
          .min(0, `${fieldLabel} must be at least 0`)
          .max(99.99, `${fieldLabel} must be at most 99.99`)
          .test(
            "max-2-decimals",
            `${fieldLabel} must have at most 2 decimal places`,
            (value) => value == null || /^\d{1,2}(\.\d{1,2})?$/.test(String(value))
          ),
      otherwise: (schema) => schema.nullable(),
    })

const SLUG_FORMAT = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

const venueSchema = Yup.object({
  name: Yup.string().required("A venue name is required"),
  slug: Yup.string()
    .required("A venue slug is required")
    .matches(
      SLUG_FORMAT,
      "Slug can only contain lowercase letters, numbers, and hyphens (no spaces)"
    ),
  currency: Yup.string().required("A currency is required"),
  contactEmail: Yup.string().trim().email("Must be a valid email").nullable(),

  enableTax: Yup.boolean(),
  taxRate: decimalRateField("Tax rate", "enableTax"),

  enableBookingSurcharge: Yup.boolean(),
  bookingSurchargeRate: decimalRateField("Booking surcharge rate", "enableBookingSurcharge"),
})

interface Option {
  value: string
  label: string
}

const CategoriesSelect = ({
  name,
  label,
  options,
  ...props
}: {
  name: string
  label: string
  options: Option[]
}) => {
  const [field] = useField(name)
  const { setFieldValue } = useFormikContext()

  const handleChange = (selectedOptions: MultiValue<Option>) => {
    setFieldValue(name, selectedOptions ? selectedOptions.map((option) => option.value) : [])
  }

  const value = field.value ? field.value.map((v: string) => ({ value: v, label: v })) : []

  return (
    <div className="form-group mb-3">
      <label className="form-label">{label}</label>
      <CreatableSelect
        isMulti
        onChange={handleChange}
        options={options}
        value={value}
        className="react-select-container"
        classNamePrefix="react-select"
        styles={reactSelectStyles}
        {...props}
      />
    </div>
  )
}

const VenueFormBody = ({
  initialValues,
  allCategoryOptions,
  venueSlug,
}: {
  initialValues?: Venue
  allCategoryOptions: Option[]
  venueSlug?: string
}) => {
  const { setFieldValue, values } = useFormikContext<any>()

  return (
    <>
      <CatchCard>
        <h4 className="card-title">Venue Details</h4>
        <FormikInput name="name" label="Venue Name" required />
        <FormikInput name="slug" label="Venue Slug" required />
        <FormikInput name="contactEmail" label="Contact Email" type="email" />
        <label htmlFor="currency" className="form-label">
          Currency (default: USD)
        </label>
        <FormikRadioGroup
          name="currency"
          label="Currency"
          options={[
            { value: "USD", label: "USD" },
            { value: "CAD", label: "CAD" },
          ]}
        />
        <CategoriesSelect name="categories" label="Categories" options={allCategoryOptions} />
      </CatchCard>

      <CatchCard>
        <h4 className="card-title">Payments</h4>

        <div className="form-check form-switch mb-3">
          <input
            id="venue-enable-saved-payments"
            type="checkbox"
            className="form-check-input"
            checked={!!values.enableSavedPayments}
            disabled={!!values.enableInstantPayments}
            onChange={(e) => {
              const checked = e.target.checked
              setFieldValue("enableSavedPayments", checked)

              if (checked) {
                setFieldValue("enableInstantPayments", false)
              } else if (!values.enableInstantPayments) {
                setFieldValue("allowSkipPayment", false)
              }
            }}
          />
          <label className="form-check-label" htmlFor="venue-enable-saved-payments">
            Enable Saved Payments
          </label>
          <div className="form-text">
            Show payment step during booking and allow staff to charge saved cards later.
            {values.enableInstantPayments
              ? " Disable Instant Payment before enabling Saved Payments."
              : " Enabling this will disable Instant Payment."}
          </div>
        </div>

        <div className="form-check form-switch mb-3">
          <input
            id="venue-enable-instant-payments"
            type="checkbox"
            className="form-check-input"
            checked={!!values.enableInstantPayments}
            disabled={!!values.enableSavedPayments}
            onChange={(e) => {
              const checked = e.target.checked
              setFieldValue("enableInstantPayments", checked)

              if (checked) {
                setFieldValue("enableSavedPayments", false)
              } else if (!values.enableSavedPayments) {
                setFieldValue("allowSkipPayment", false)
              }
            }}
          />
          <label className="form-check-label" htmlFor="venue-enable-instant-payments">
            Enable Instant Payment
          </label>
          <div className="form-text">
            Charge fans upfront during booking.
            {values.enableSavedPayments
              ? " Disable Saved Payments before enabling Instant Payment."
              : " Enabling this will disable Saved Payments."}
          </div>
        </div>

        <div className="form-check form-switch mb-2">
          <input
            id="venue-allow-skip-payment"
            type="checkbox"
            className="form-check-input"
            checked={!!values.allowSkipPayment}
            disabled={!values.enableSavedPayments && !values.enableInstantPayments}
            onChange={(e) => setFieldValue("allowSkipPayment", e.target.checked)}
          />
          <label className="form-check-label" htmlFor="venue-allow-skip-payment">
            Allow Skip Payment
          </label>
          <div className="form-text">
            Let fans create a booking without completing the active payment step.
          </div>
        </div>

        {venueSlug && <StripeConnectSection venueSlug={venueSlug} />}
      </CatchCard>

      <CatchCard>
        <h4 className="card-title">Pricing & Taxes</h4>

        <div className="form-check form-switch mb-3">
          <input
            id="venue-enable-tax"
            type="checkbox"
            className="form-check-input"
            checked={!!values.enableTax}
            onChange={(e) => {
              const checked = e.target.checked
              setFieldValue("enableTax", checked)

              if (!checked) {
                setFieldValue("taxRate", "")
              }
            }}
          />
          <label className="form-check-label" htmlFor="venue-enable-tax">
            Enable Tax
          </label>
          <div className="form-text">Apply a tax rate to booking totals.</div>
        </div>

        {values.enableTax && (
          <div className="mb-4">
            <FormikInput
              name="taxRate"
              label="Tax Rate (%)"
              placeholder="8.25"
              type="number"
              step="0.01"
              min="0"
              max="99.99"
            />
          </div>
        )}

        <div className="form-check form-switch mb-3">
          <input
            id="venue-enable-booking-surcharge"
            type="checkbox"
            className="form-check-input"
            checked={!!values.enableBookingSurcharge}
            onChange={(e) => {
              const checked = e.target.checked
              setFieldValue("enableBookingSurcharge", checked)

              if (!checked) {
                setFieldValue("bookingSurchargeRate", "")
              }
            }}
          />
          <label className="form-check-label" htmlFor="venue-enable-booking-surcharge">
            Enable Booking Surcharge
          </label>
          <div className="form-text">
            Apply a percentage-based surcharge to booking totals before tax.
          </div>
        </div>

        {values.enableBookingSurcharge && (
          <div className="mb-2">
            <FormikInput
              name="bookingSurchargeRate"
              label="Surcharge Rate (%)"
              placeholder="20.00"
              type="number"
              step="0.01"
              min="0"
              max="99.99"
            />
          </div>
        )}
      </CatchCard>

      <CatchCard>
        <h4 className="card-title">Address</h4>
        <FormikInput name="address1" label="Address 1" />
        <FormikInput name="address2" label="Address 2" />
        <FormikInput name="city" label="City" />
        <FormikInput name="state" label="State" />
        <FormikInput name="postalCode" label="Postal Code" />
        <FormikInput name="country" label="Country" />
      </CatchCard>

      <CatchCard>
        <h4 className="card-title">Logo</h4>
        <ImageUpload
          currentImageUrl={initialValues?.logoUrl}
          alt={initialValues?.logo?.alt}
          title={initialValues?.logo?.title}
          onUpload={(newImage: Image) => {
            setFieldValue("logo_id", newImage.id)
          }}
        />
      </CatchCard>

      <div className="d-flex">
        <div className="ms-auto">
          <Link className="btn btn-secondary me-2" to="/">
            Cancel
          </Link>
          <button type="submit" className="btn btn-primary">
            Submit
          </button>
        </div>
      </div>
    </>
  )
}

const VenueForm = ({
  onSubmit,
  initialValues,
  isEditing = false,
  venueSlug,
}: {
  onSubmit: (values: any) => void
  initialValues?: Venue
  isEditing: boolean
  venueSlug?: string
}) => {
  const defaultValues = {
    name: "",
    slug: "",
    contactEmail: "",
    currency: "USD",
    address1: "",
    address2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    categories: [],
    logo_id: null,

    enableSavedPayments: false,
    enableInstantPayments: false,
    allowSkipPayment: false,

    enableTax: false,
    taxRate: "",

    enableBookingSurcharge: false,
    bookingSurchargeRate: "",
  }

  const baseCategories = [
    { value: "Wedding", label: "Wedding" },
    { value: "Corporate", label: "Corporate" },
    { value: "Private", label: "Private" },
  ]

  const existingCategories = (initialValues?.categories || []).map((c: string) => ({
    value: c,
    label: c,
  }))

  const allCategoryOptions = [...baseCategories]
  existingCategories.forEach((existingCat: Option) => {
    if (!allCategoryOptions.find((opt) => opt.value === existingCat.value)) {
      allCategoryOptions.push(existingCat)
    }
  })

  const formValues =
    isEditing && initialValues
      ? {
          ...initialValues,
          contactEmail: initialValues.contactEmail ?? "",
          logo_id: initialValues.logo?.id,
          taxRate: initialValues.taxRate ?? "",
          bookingSurchargeRate: initialValues.bookingSurchargeRate ?? "",
        }
      : defaultValues

  return (
    <FormikWrapper
      onSubmit={onSubmit}
      validationSchema={venueSchema}
      initialValues={formValues}
      enableReinitialize
    >
      <VenueFormBody
        initialValues={initialValues}
        allCategoryOptions={allCategoryOptions}
        venueSlug={venueSlug}
      />
    </FormikWrapper>
  )
}

export default VenueForm
